import type { UiTagColor } from '../../atoms/tag/tag.component';
import { DataGridColumn, DataTableTagItem } from './data-table.types';
import { formatAmountNumber, formatCurrency, formatDate, formatNumber } from './data-table.formatters';

const TAG_ESTATUS_COLORS: Record<string, UiTagColor> = {
  Activo: 'success',
  Pendiente: 'warning',
  Inactivo: 'neutral',
};

const LEGACY_STATUS_COLORS: Record<string, UiTagColor> = {
  'dg-status-active': 'success',
  'dg-status-aprobado-tesoreria': 'success',
  'dg-status-inactive': 'neutral',
  'dg-status-pending': 'warning',
  'dg-status-pendiente': 'warning',
  'dg-status-apartado': 'warning',
  'dg-status-cancelled': 'danger',
  'dg-status-libre': 'primary',
  'dg-status-ocupado': 'warning',
};

/**
 * Devuelve el valor crudo de una celda para la columna dada.
 */
export function getCellValue(row: unknown, col: DataGridColumn): unknown {
  return (row as Record<string, unknown>)[col.key];
}

/** Devuelve el valor de la celda como etiqueta segura para componentes. */
export function getCellLabel(row: unknown, col: DataGridColumn): string {
  return String(getCellValue(row, col) ?? '');
}

/**
 * Devuelve la clase CSS para la celda según `cellClassMap` de la columna.
 * Retorna cadena vacía si la columna no tiene mapa de clases.
 */
export function getCellClass(row: unknown, col: DataGridColumn): string {
  if (!col.cellClassMap) return '';
  const v = String(getCellValue(row, col) ?? '');
  return col.cellClassMap[v] ?? '';
}

/**
 * Resuelve el color semántico del tag. Prioriza la API nueva y traduce las
 * clases históricas para conservar todas las tablas existentes.
 */
export function getCellTagColor(
  row: unknown,
  col: DataGridColumn,
): UiTagColor | null {
  const value = String(getCellValue(row, col) ?? '');
  const configuredColor = col.tagColorMap?.[value];
  if (configuredColor) return configuredColor;

  const legacyClass = col.cellClassMap?.[value];
  if (!legacyClass) return null;
  return LEGACY_STATUS_COLORS[legacyClass] ?? 'neutral';
}

/**
 * Devuelve el texto del tooltip para la celda según `tooltipByKey` / `tooltipMap`.
 * Retorna cadena vacía si la columna no tiene configuración de tooltip.
 */
export function getCellTooltip(row: unknown, col: DataGridColumn): string {
  if (!col.tooltipByKey || !col.tooltipMap) return '';
  const key = String((row as Record<string, unknown>)[col.tooltipByKey] ?? '');
  return col.tooltipMap[key] ?? '';
}

/**
 * Convierte el valor de una celda de tipo `projects` en un array de strings.
 * Acepta un array nativo o una cadena separada por comas.
 */
export function getCellTags(row: unknown, col: DataGridColumn): DataTableTagItem[] {
  const raw = getCellValue(row, col);
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (item): item is DataTableTagItem =>
      Boolean(item) &&
      typeof item === 'object' &&
      typeof (item as DataTableTagItem).label === 'string' &&
      typeof (item as DataTableTagItem).estatus === 'string',
  );
}

export function getTagEstatusColor(estatus: string): UiTagColor {
  return TAG_ESTATUS_COLORS[estatus] ?? 'neutral';
}

export function getProjectsList(v: unknown): string[] {
  if (!v) return [];
  if (Array.isArray(v)) return v.map(String);
  return String(v).split(',').map(s => s.trim()).filter(Boolean);
}

/**
 * Devuelve la representación textual de una celda para usarla en el
 * algoritmo de cálculo de anchos de columna.
 */
export function getCellText(row: Record<string, unknown>, col: DataGridColumn): string {
  if (col.iconButtons?.length) {
    return col.iconButtons.map(btn => btn.ariaLabel).join(' ');
  }
  const raw = row[col.key];
  if (raw == null) return '—';
  if (col.type === 'date')     return formatDate(raw);
  if (col.type === 'number') {
    return col.editType === 'amount' ? formatAmountNumber(raw) : formatNumber(raw);
  }
  if (col.type === 'currency') return formatCurrency(raw);
  if (col.type === 'button') {
    return col.buttonLabels?.length
      ? col.buttonLabels.join(' ')
      : (col.buttonLabel ?? col.header);
  }
  if (col.type === 'string-list') {
    return getProjectsList(raw).reduce(
      (longest, item) => item.length > longest.length ? item : longest,
      '',
    );
  }
  if (col.type === 'tags') {
    return getCellTags(row, col)
      .map(tag => tag.label)
      .join('\n');
  }
  if (col.type === 'boolean') return raw ? (col.trueLabel ?? 'Sí') : (col.falseLabel ?? 'No');
  if (col.type === 'link' && col.linkKey) {
    return `${String(raw ?? '—')}  ${col.linkLabel ?? 'Ver'}`;
  }
  return String(raw);
}
